import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Icon, CreativeLabels } from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";

import { hasPermission } from "utils/common";
import {
	groupLabelsByCategory,
	sortCategoryGroups
} from "utils/Labels";
import useQueryParams from "hooks/useQueryParams";

import {
	loadCreativeLabels,
	loadVersionLabels,
	deleteCreativeLabels,
	deleteVersionLabels
} from "reducers/Labels";
import { getManualClassificationEnabled } from "selectors/Intelligence";

import Permissioned from "widgets/Permissioned";
import ProgressContainer from "widgets/ProgressContainer";
import LabelManagementForm from "./LabelManagementForm";

import NoLabelsView from "./NoLabelsView";

import "./LabelsPanel.css";

export default function LabelsPanel(props){
	const {
		libraryId,
		creativeId,
		versionId,
		versionDetails,
	} = props;
	const dispatch = useDispatch();
	const { getParam, setParam } = useQueryParams();
	const showLabels = getParam("showLabels", "true");
	const isLoading = useSelector(state => state.labels?.isLoading);
	const labels = useSelector((state) => {
		if(versionId){
			return state.labels?.versions?.[versionId];
		}
		return state.labels?.creatives?.[creativeId];
	});
	const canViewLabels = useSelector(getManualClassificationEnabled);
	const canEditLabels = useSelector(state => hasPermission(state.you, "Creative Library", "Edit"));

	const [showLabelsForm, setShowLabelsForm] = useState(false);

	useEffect(() => {
		if(!canViewLabels){ return; }
		if(versionId){
			dispatch(loadVersionLabels(creativeId, [versionId]));
		} else {
			dispatch(loadCreativeLabels(libraryId, [creativeId]));
		}
	}, [canViewLabels, libraryId, creativeId, versionId]);

	useEffect(() => {
		if(canEditLabels && !isLoading && !labels?.length){
			setShowLabelsForm(true);
		} else {
			setShowLabelsForm(false);
		}
	}, [canEditLabels, labels, isLoading]);

	const groupedLabels = useMemo(() => {
		return groupLabelsByCategory(labels);
	}, [labels]);

	let labelsContent = (
		<NoLabelsView
			libraryId={libraryId}
			creativeId={creativeId}
			versionId={versionId}
			versionDetails={versionDetails}
		/>
	);

	function removeLabel(value){
		const { id } = value || {};
		if(versionId){
			dispatch(deleteVersionLabels(versionId, [id]));
		} else {
			dispatch(deleteCreativeLabels(creativeId, [id]));
		}
	}

	const categoryGroups = Object.keys(groupedLabels);
	if (categoryGroups.length) {
		labelsContent = categoryGroups
			.sort(sortCategoryGroups)
			.map((groupName) => {
				const labelList = groupedLabels[groupName];
				return (
					<CreativeLabels
						key={groupName}
						header={groupName}
						labels={labelList}
						labelValue="prettyName"
						allowRemove={canEditLabels}
						group={groupName}
						onRemove={removeLabel}
					/>
				);
			});
	}

	if(!showLabels || !canViewLabels){
		/*
			This component uses the "showLabels" query param as the single source of truth
			for whether or not to render anything, as it will need to be controlled from a variety
			of inputs, including direct links into the app.
		*/
		return null;
	}

	const $form = showLabelsForm ? <LabelManagementForm {...props} /> : null;

	return (
		<div className="labels-panel">
			<div className="labels-panel-header">
				<div className="title">Creative Labels {`(${labels?.length || 0})`}</div>
				<Permissioned domain="Creative Library" action="Edit">
					<div
						className="button"
						onClick={() => setShowLabelsForm(!showLabelsForm)}
					>
						<Icon name="far fa-plus-circle" />
					</div>
				</Permissioned>
				<div
					className="button"
					onClick={() => setParam("showLabels", "false", true)}
				>
					<Icon name="clear" />
				</div>
			</div>
			<ProgressContainer isLoading={isLoading}>
				{$form}
				{labelsContent}
			</ProgressContainer>
		</div>
	);
}

const ID_TYPE = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

LabelsPanel.displayName = "LabelsPanel";
LabelsPanel.propTypes = {
	libraryId: ID_TYPE,
	creativeId: ID_TYPE,
	versionId: ID_TYPE,
};
